import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@getgreenline/blaze-ui";
import { Check, LogOut, Monitor, Moon, Shield, Sun } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { type ThemeMode } from "../../hooks/use-theme";

type UserMenuProps = {
  accountName: string;
  userName: string;
  userEmail: string;
  userInitials: string;
  avatarUrl?: string;
  theme: ThemeMode;
  onThemeChange: (theme: ThemeMode) => void;
  onLogout?: () => void;
  showBackoffice?: boolean;
};

export function UserMenu({
  accountName,
  userName,
  userEmail,
  userInitials,
  avatarUrl,
  theme,
  onThemeChange,
  onLogout,
  showBackoffice = false,
}: UserMenuProps) {
  const navigate = useNavigate();
  const themeIcon =
    theme === "light" ? (
      <Sun className="tw:size-4" />
    ) : theme === "system" ? (
      <Monitor className="tw:size-4" />
    ) : (
      <Moon className="tw:size-4" />
    );

  const themeOptions: Array<{
    value: ThemeMode;
    label: string;
    icon: typeof Sun;
  }> = [
    { value: "light", label: "Light", icon: Sun },
    { value: "dark", label: "Dark", icon: Moon },
    { value: "system", label: "System", icon: Monitor },
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className="tw:h-10 tw:cursor-pointer tw:gap-2 tw:rounded-xl tw:px-2"
        >
          <Avatar className="tw:h-8 tw:w-8 tw:shrink-0">
            <AvatarImage src={avatarUrl} alt={userName} />
            <AvatarFallback className="tw:text-xs tw:font-semibold">
              {userInitials}
            </AvatarFallback>
          </Avatar>
          <div className="tw:hidden tw:min-w-0 tw:text-left lg:tw:block">
            <p className="tw:max-w-[140px] tw:truncate tw:text-sm tw:font-medium tw:text-foreground">
              {userName}
            </p>
            <p className="tw:max-w-[140px] tw:truncate tw:text-xs tw:text-muted-foreground">
              {accountName}
            </p>
          </div>
          <span className="tw:sr-only">Open user menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="tw:w-64" side="bottom" align="end">
        <DropdownMenuLabel className="tw:flex tw:items-center tw:gap-3">
          <Avatar className="tw:h-10 tw:w-10">
            <AvatarImage src={avatarUrl} alt={userName} />
            <AvatarFallback className="tw:text-sm tw:font-semibold">
              {userInitials}
            </AvatarFallback>
          </Avatar>
          <div className="tw:min-w-0 tw:leading-tight">
            <div className="tw:truncate tw:text-sm tw:font-semibold tw:text-foreground">
              {userName}
            </div>
            <div className="tw:truncate tw:text-xs tw:text-muted-foreground">
              {userEmail}
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {showBackoffice ? (
            <DropdownMenuItem onClick={() => navigate("/backoffice")}>
              <Shield className="tw:size-4" />
              <span>Backoffice</span>
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              {themeIcon}
              <span>Theme</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {themeOptions.map(({ value, label, icon: Icon }) => (
                <DropdownMenuItem
                  key={value}
                  onClick={() => onThemeChange(value)}
                >
                  <Icon className="tw:size-4" />
                  <span>{label}</span>
                  {theme === value ? (
                    <Check className="tw:ml-auto tw:size-4" />
                  ) : null}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onClick={onLogout}>
          <LogOut className="tw:size-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
